/**
 * No longer handles unauthenticated redirects
 * Focuses on role-based authorization
 */
import * as Sentry from '@sentry/react'
import { type ReactElement, type ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'

import { minimumRequiredRole } from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import {
  AWXConnectedAccountStatus,
  type AwxConnectedAccount,
  type AwxConnectedAccountType,
} from '@ankor-io/common/types/awx'
import { Loading } from '@ankor-io/ui-components/src/Loading'

import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { testAwareMs } from '@/utils/testAwareMs'

/**
 * Switches the active connected account to the target and shows loading.
 * After the context update, ProtectedRoute re-renders and the module check passes.
 */
const SwitchModuleAndContinue = ({ targetConnectedAccountId }: { targetConnectedAccountId: string }) => {
  const { updateActiveConnectedAccountId } = useUserContext()
  useEffect(() => {
    updateActiveConnectedAccountId(targetConnectedAccountId)
  }, [targetConnectedAccountId, updateActiveConnectedAccountId])
  return <Loading />
}

interface ProtectedRouteProps {
  children: ReactNode
  /** Specify allowed roles for a route. This overrides minRole. */
  allowedRoles?: Role[]
  /** Specify the minimum role required to access a route. */
  minRole?: Role
  /** Require the active connected account to be of this module type (APA or OPERATIONS). */
  requiredModule?: AwxConnectedAccountType
  /**
   * Require the active vessel to be one the user can actually access.
   * Mirrors the backend's vessel-scoped gate (e.g. charter management): a VESSEL_ADMIN only
   * passes when their active vessel is UserVessel-linked; privileged roles pass via their
   * org-wide accessible-vessel list. Prevents rendering a page whose data call will 403.
   */
  requireVesselAccess?: boolean
  /** optional path to send users who are authenticated but not authorized (403 Forbidden) */
  forbiddenPath?: string
  /** optional loading indicator */
  fallback?: ReactNode
}

/**
 * Checks if a pathname requires connected account validation.
 * Routes that require active connected accounts with approver role.
 */
const requiresConnectedAccountValidation = (pathname: string): boolean => {
  return pathname.startsWith('/wallet/') || pathname.startsWith('/cards')
}

/**
 * Validates that the user has access to routes requiring connected account validation.
 * Applies the same checks as Layout.tsx for menu item disabled states.
 */
const validateConnectedAccountAccess = (
  connectedAccounts: AwxConnectedAccount[],
  activeConnectedAccount: AwxConnectedAccount | null,
  activeOrganizationRole: Role,
): boolean => {
  const hasNoConnectedAccounts = connectedAccounts.length === 0
  const isCurrentConnectedAccountActive = activeConnectedAccount?.status === AWXConnectedAccountStatus.ACTIVE

  return (
    !hasNoConnectedAccounts &&
    isCurrentConnectedAccountActive &&
    minimumRequiredRole(activeOrganizationRole, Role.APPROVER)
  )
}

export function ProtectedRoute({
  children,
  allowedRoles,
  minRole,
  requiredModule,
  requireVesselAccess,
  forbiddenPath = '/forbidden',
  fallback = <Loading />,
}: ProtectedRouteProps) {
  const { isLoading, organizations, activeOrganizationId, activeVesselId, activeOrganizationRole } = useUserContext()
  const {
    connectedAccounts,
    activeConnectedAccount,
    activeModule,
    accessibleVesselIds = [],
    isLoading: isLoadingConnectedAccounts,
  } = useConnectedAccount()

  const isAnyLoading = isLoading || isLoadingConnectedAccounts
  const showLoader = useMinimumDisplayTime(isAnyLoading, 1000)

  const location = useLocation()

  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Listen for logout events to set the logging out state
  useEffect(() => {
    const handleLogout = () => {
      setIsLoggingOut(true)
    }

    // Listen for custom logout events
    window.addEventListener('user-logout', handleLogout)

    return () => {
      window.removeEventListener('user-logout', handleLogout)
    }
  }, [])

  // Listen for organization and connected account switching events
  useEffect(() => {
    const handleOrganizationSwitching = () => {
      setIsTransitioning(true)
    }

    const handleVesselSwitching = () => {
      setIsTransitioning(true)
    }

    // Listen for custom switching events
    window.addEventListener('organization-switching', handleOrganizationSwitching)
    window.addEventListener('vessel-switching', handleVesselSwitching)

    return () => {
      window.removeEventListener('organization-switching', handleOrganizationSwitching)
      window.removeEventListener('vessel-switching', handleVesselSwitching)
    }
  }, [])

  // Clear transitioning state when loading completes
  useEffect(() => {
    if (isTransitioning && !isLoading && !isLoadingConnectedAccounts) {
      // Use a small timeout to ensure state has updated
      const timeoutId = setTimeout(() => {
        setIsTransitioning(false)
      }, testAwareMs(100))

      return () => clearTimeout(timeoutId)
    }
  }, [isTransitioning, isLoading, isLoadingConnectedAccounts])

  // waiting for auth to resolve
  if (showLoader) {
    return fallback
  }

  const activeOrg = activeOrganizationId ? organizations[activeOrganizationId] : null

  // If no active organization, redirect to unauthorized (unless logging out or transitioning)
  if (!activeOrg && !isLoggingOut && !isTransitioning) {
    return (
      <Navigate
        to={forbiddenPath}
        replace
      />
    )
  }

  /**
   * Vessel-scoped routes (e.g. charter management) must agree with the backend's vessel gate:
   * the active vessel has to be one the user can actually access. The accessible-vessel list is
   * already role-scoped by the backend, so privileged roles pass naturally while an unlinked
   * VESSEL_ADMIN is stopped — instead of rendering a page whose data call 403s into a
   * "Ledger not found" dead end. We only enforce once the vessel list has loaded and a stale/
   * mismatched active vessel is actually set; a null active vessel is handled by other flows.
   *
   * Evaluated only after the role/module checks pass, so an under-privileged user is redirected
   * by the role gate (with correct telemetry) rather than mislabelled as a vessel-access failure.
   */
  const validateVesselAccess = (): ReactElement | null => {
    if (!requireVesselAccess || isLoggingOut || isTransitioning) return null
    if (!activeVesselId || accessibleVesselIds.includes(activeVesselId)) return null

    Sentry.captureMessage('Blocked vessel-scoped route: active vessel not accessible to user', {
      level: 'warning',
      extra: {
        activeVesselId,
        activeOrganizationId,
        accessibleVesselCount: accessibleVesselIds.length,
        pathname: location.pathname,
      },
    })
    return (
      <Navigate
        to={forbiddenPath}
        replace
      />
    )
  }

  /** Redirect/switch when there is no ACTIVE account of required type: switch to KYC account or redirect to /kyc-rfi or / */
  const NoActiveAccountRedirect = (): ReactElement => {
    // Vessel has a connected account of this module type that's in KYC (CREATED).
    // If so, we send the user to /kyc-rfi instead of / to avoid a redirect loop with ModuleRedirect.
    const accountNeedingKyc = connectedAccounts.find(
      (a) => a.type === requiredModule && a.status === AWXConnectedAccountStatus.CREATED,
    )
    const isAccountNeedingKycActive = accountNeedingKyc && activeConnectedAccount?.id === accountNeedingKyc.id

    // Active account is a different one (e.g. OPS). Switch to the account needing KYC so /kyc-rfi shows the right context.
    if (accountNeedingKyc && !isAccountNeedingKycActive) {
      return <SwitchModuleAndContinue targetConnectedAccountId={accountNeedingKyc.id} />
    }
    // Active account is the one needing KYC; send user to complete it.
    if (accountNeedingKyc && isAccountNeedingKycActive) {
      return (
        <Navigate
          to="/kyc-rfi"
          replace
        />
      )
    }
    // No account of this module in KYC state; send to / and let ModuleRedirect choose the dashboard.
    return (
      <Navigate
        to="/"
        replace
      />
    )
  }

  /** Statuses that allow rendering the module dashboard (ACTIVE or KYC in progress with view) */
  const STATUSES_ALLOWING_DASHBOARD: AWXConnectedAccountStatus[] = [
    AWXConnectedAccountStatus.ACTIVE,
    AWXConnectedAccountStatus.SUBMITTED,
    AWXConnectedAccountStatus.ACTION_REQUIRED,
  ]

  /**
   * Validates module access when requiredModule is specified.
   * Access is allowed when there is a connected account of the required type with status ACTIVE,
   * SUBMITTED, or ACTION_REQUIRED (dashboard is visible; user can open /kyc-rfi via CTA if needed).
   * Only CREATED (initial KYC not started) triggers redirect to /kyc-rfi via NoActiveAccountRedirect.
   * If the user's active connected account is the wrong type, switches to an account of the required
   * type that allows dashboard access. Otherwise redirects to `/` or /kyc-rfi.
   */
  const validateModuleAccess = (): ReactElement | null => {
    if (!requiredModule || isLoggingOut || isTransitioning) {
      return null
    }

    const targetAccount = connectedAccounts.find(
      (a) => a.type === requiredModule && STATUSES_ALLOWING_DASHBOARD.includes(a.status as AWXConnectedAccountStatus),
    )

    if (!targetAccount) {
      return <NoActiveAccountRedirect />
    }

    if (activeModule !== requiredModule) {
      const targetAccount = connectedAccounts.find(
        (a) => a.type === requiredModule && STATUSES_ALLOWING_DASHBOARD.includes(a.status as AWXConnectedAccountStatus),
      )

      if (targetAccount) {
        return <SwitchModuleAndContinue targetConnectedAccountId={targetAccount.id} />
      }

      return <NoActiveAccountRedirect />
    }

    return null
  }

  // Check if user has any of the allowed roles
  if (allowedRoles?.length && activeOrg?.roles.some((userRole) => allowedRoles.includes(userRole))) {
    // Check if route requires connected account validation (wallet, cards, etc.)
    if (requiresConnectedAccountValidation(location.pathname) && !isLoggingOut && !isTransitioning) {
      const hasAccess = validateConnectedAccountAccess(
        connectedAccounts,
        activeConnectedAccount,
        activeOrganizationRole,
      )

      if (!hasAccess) {
        return (
          <Navigate
            to={forbiddenPath}
            replace
          />
        )
      }
    }

    const moduleBlock = validateModuleAccess()
    if (moduleBlock) return moduleBlock

    const vesselBlock = validateVesselAccess()
    if (vesselBlock) return vesselBlock

    return <>{children}</>
  }

  if (minRole && activeOrg) {
    // Role is an enum that is ordered from highest to lowest
    // We can convert this enum into an array and use the index to compare the user's role with the minimum required role
    const roleHierarchy = Object.values(Role) as Role[]
    const userRoleIndex = roleHierarchy.indexOf(activeOrg.roles[0])
    const minRoleIndex = roleHierarchy.indexOf(minRole)

    if (userRoleIndex <= minRoleIndex) {
      // Check if route requires connected account validation (wallet, cards, etc.)
      if (requiresConnectedAccountValidation(location.pathname) && !isLoggingOut && !isTransitioning) {
        const hasAccess = validateConnectedAccountAccess(
          connectedAccounts,
          activeConnectedAccount,
          activeOrganizationRole,
        )

        if (!hasAccess) {
          return (
            <Navigate
              to={forbiddenPath}
              replace
            />
          )
        }
      }

      const moduleBlock = validateModuleAccess()
      if (moduleBlock) return moduleBlock

      const vesselBlock = validateVesselAccess()
      if (vesselBlock) return vesselBlock

      return <>{children}</>
    }
  }

  // By default, redirect to 403 Forbidden (unless logging out or transitioning)
  if (!isLoggingOut && !isTransitioning) {
    return (
      <Navigate
        to={forbiddenPath}
        replace
      />
    )
  }

  // If logging out or transitioning, just show loading or nothing
  return fallback
}
